import
  React, {
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import {
  Route,
  Router,
  Switch,
  useHistory,
  withRouter
} from 'react-router-dom';

import
  styled
from 'styled-components';

import {
  Footer,
  Header,
  TopBanner
} from './components';

import {
  Basic,
  FAQ
} from './pages';

import {
  FreeServices,
  NewsFeed,
  Search,
  WeatherDetail
} from './journeys';

import {
  DrupalProvider
} from './providers';

import {
  CacheKeys,
  WebRoutes
} from './constants';

import {
  MsisdnCheck,
  ScrollToTop
} from './core';

import {
  isExternalUrl,
  isFeatureEnabled
} from './helpers';

import {
  AppContext
} from './context';

const AppStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: 100vh;
`;

const components = {
  'FreeServices': FreeServices,
  'Weather': WeatherDetail,
  'Search': Search,
  'Newsfeed': NewsFeed,
  'FAQ': FAQ
};

const AdsComponent  = lazy(() => import('./components/ads/Ads'));

const App = withRouter(({ location }) => {

  const { config } = useContext(AppContext);
  const history = useHistory();
  const [ externalItems, setExternalItems ] = useState([]);
  const [ menuItems, setMenuItems ] = useState([]);
  const [ menuRoutes, setMenuRoutes ] = useState({
    pageRoutes: null,
    CMSRoutes: null
  });

  const PageRoute = (route, i) => {

    const isEnabled = isFeatureEnabled(config, route.featureToggle);

    if (!route.featureToggle || isEnabled) {

      return (

        <Route
          exact
          key={`route-${i}`}
          path={route.path}
          component={route.component}/>
      );
    }

    return;
  };

  const buildRoutes = useCallback((menuItems) => {

    if (!menuItems || menuItems.length === 0) {
      return;
    }

    const pageRoutes = [];
    const CMSRoutes = [];
    const externalLinks = [];

    for (const item of menuItems) {

      if (item.hasCMSPage) {
        CMSRoutes.push(item);
        continue;
      }

      const isExternal = isExternalUrl(item.url);

      pageRoutes.push({
        ...item,
        isExternal: isExternal
      });

      if (isExternal) {
        externalLinks.push(item.url);
      }
    }

    setExternalItems(externalLinks);

    const cmsRoute = <Route
      exact
      path={CMSRoutes.map(page => page.url)}
      component={Basic} />;

    const pageRoute = pageRoutes.flatMap(page => {

      if (!page.isExternal && !!components[page.component]) {

        return <Route
          exact
          key={page.id}
          path={page.url}
          component={components[page.component]} />;
      }

      return [];
    });

    setMenuRoutes({
      ...menuRoutes,
      CMSRoutes: cmsRoute,
      pageRoutes: pageRoute
    });
  }, [ menuRoutes ]);

  const getMenu = useCallback(async() => {

    if (menuRoutes.CMSRoutes && menuRoutes.pageRoutes) {
      return;
    }

    const response = await DrupalProvider.getCachedItem(CacheKeys.Menu, null, DrupalProvider.getMenu);

    if (!response?.ok || !response?.result) {
      return;
    }

    setMenuItems(response.result);

    buildRoutes(response.result);

  }, [ buildRoutes, menuRoutes.CMSRoutes, menuRoutes.pageRoutes ]);

  useEffect(() => {

    getMenu();

  }, [ getMenu ]);

  const excludeHeader = [
    /^\/$/,
    /^\/landing$/,
    /^\/redirect$/,
    /^\/landing\/legal$/,
    /^\/advance\/payment$/,
    /^\/advance\/legal$/,
    /^\/education-registration$/,
    /^\/voucher-advance\/[\d]+\/[\w\d-]+$/,
    /^\/voucher-advance\/order\/summary$/,
    /^\/voucher-advance\/order\/complete\/$/
  ];

  return (

    <Router
      history={history}>

      <MsisdnCheck />
      <ScrollToTop />
      <Suspense fallback={<></>}>
        <AdsComponent />
      </Suspense>

      <AppStyled>
        { !excludeHeader.some(path => {

          return path.test(location.pathname);
        }) &&

          <>

            <TopBanner/>

            <Header
              externalLinks={externalItems}
              menuItems={[
              {
                label: 'Home',
                url: '/home'
                },
              ...menuItems
            ]} />
          </>
        }

          <Switch>
            {
              menuRoutes?.CMSRoutes
            }
            {
              menuRoutes?.pageRoutes?.length > 0 &&
              menuRoutes.pageRoutes.map(route => route)
            }

            {
              WebRoutes && WebRoutes.map((route, i) => (
                PageRoute(route, i)
              ))
            }

          </Switch>

        { !excludeHeader.some(path => {

            return location.pathname === path;
          }) &&

          <Footer />
        }

      </AppStyled>
    </Router>
  );
});

export default App;
